/* eslint-disable no-console */
import { useEffect, useState } from "react";
import PageTitle from "../../components/PageTitle";
import { toast } from "react-toastify";
import { Box, InputBase, Menu, MenuItem, Paper } from "@mui/material";
import Layout from "../../components/Layout";
import PopModal from "../../components/PopupModal/PopModal";
import MuiButton from "@mui/material/Button";
import { Icon } from "@iconify/react";
import emptynotification from "../../assets/images/emptynotification.png";
import emptyDetails from "../../assets/images/emptydetail.png";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import useStyles from "./styles";
import logo from "../../assets/images/shelflogo.png";
import { Search, KeyboardArrowDown } from "@mui/icons-material";
import { httprequest } from "../../data/api";
import NoData from "../dashboard/components/NotFoundCard/NoData";
import { useSearchParams } from "react-router-dom";

const ALL_NOTIFICATION = "/api/v1/notifications";

const getInitials = (name) => {
  if (!name) return "S";
  const parts = name.split(" ");
  if (parts.length > 1) {
    return parts[0][0] + parts[1][0];
  }

  return parts[0].substring(0, 2);
};

const formatDateTime = (isoString) => {
  const date = new Date(isoString);

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  });
};

// Helper function to extract title from HTML
const extractTitleFromHTML = (html) => {
  const titleMatch = html.match(/<title[^>]*>([^<]+)<\/title>/i) ||
                    html.match(/<h\d[^>]*>([^<]+)<\/h\d>/i) ||
                    html.match(/<p[^>]*class=["']?template-heading["']?[^>]*>([^<]+)<\/p>/i) ||
                    html.match(/FINAL REMINDER: ([^<]+)/i) ||
                    html.match(/<strong[^>]*>([^<]+)<\/strong>/i);

  return titleMatch ? titleMatch[1].trim() : "Notification";
};

const extractSnippetFromHTML = (html) => {
  let plainText = html
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const contentMatch = html.match(/Dear[^<]*<p>([\s\S]*?)<p>Best regards/i) ||
                      html.match(/<section[^>]*>([\s\S]*?)<\/section>/i) ||
                      html.match(/<div[^>]*class=["']?section-body["']?[^>]*>([\s\S]*?)<\/div>/i);

  if (contentMatch) {
    plainText = contentMatch[1]
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  plainText = plainText
    .replace(/^Dear\s+\w+,?\s*/i, "")
    .replace(/\s*Best regards.*$/i, "")
    .replace(/\s*-\s*The\s+shelf21\s+team.*$/i, "")
    .replace(/\s*If you see any suspicious activity.*$/i, "")
    .trim();

  if (plainText.length > 150) {
    plainText = `${plainText.substring(0, 150).trim()}...`;
  }

  return plainText || "Click to view details";
};

const parseMessageContent = (message) => {
  try {
    const parsed = JSON.parse(message);

    return {
      title: parsed.title || "Notification",
      body: parsed.body || message,
      snippet: parsed.body ?
        (parsed.body.length > 100 ? `${parsed.body.substring(0, 100)}...` : parsed.body)
        : "",
      isHtml: false
    };
  } catch (error) {
    const title = extractTitleFromHTML(message);
    const snippet = extractSnippetFromHTML(message);

    return {
      title,
      body: message,
      snippet: snippet.length > 100 ? `${snippet.substring(0, 100)}...` : snippet,
      isHtml: true
    };
  }
};

// Helper function to group notifications by date
const groupNotificationsByDate = (notifications) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const thisWeek = new Date(today);
  thisWeek.setDate(thisWeek.getDate() - 7);

  const thisMonth = new Date(today);
  thisMonth.setDate(thisMonth.getDate() - 30);

  const groups = {
    today: [],
    yesterday: [],
    thisWeek: [],
    thisMonth: [],
    older: []
  };

  notifications.forEach(notification => {
    const notificationDate = new Date(notification.created_at);
    notificationDate.setHours(0, 0, 0, 0);

    if (notificationDate.getTime() === today.getTime()) {
      groups.today.push(notification);
    } else if (notificationDate.getTime() === yesterday.getTime()) {
      groups.yesterday.push(notification);
    } else if (notificationDate > thisWeek) {
      groups.thisWeek.push(notification);
    } else if (notificationDate > thisMonth) {
      groups.thisMonth.push(notification);
    } else {
      groups.older.push(notification);
    }
  });

  return groups;
};

// Helper function to format group titles
const getGroupTitle = (groupKey) => {
  const titles = {
    today: "TODAY",
    yesterday: "YESTERDAY",
    thisWeek: "THIS WEEK",
    thisMonth: "THIS MONTH",
    older: "OLDER"
  };

  return titles[groupKey] || groupKey;
};

// Helper function to extract unique sections
const getUniqueSections = (notifications) => {
  const sections = new Set();
  notifications.forEach(notification => {
    if (notification.section) {
      sections.add(notification.section);
    }
  });

  return Array.from(sections);
};

// Helper function to get section display names
const getSectionDisplayName = (section) => {
  const displayNames = {
    "payment": "Payment",
    "events": "Events",
    "student": "Student",
    "employee": "Employee",
    "exam timetable": "Exam Timetable",
    "class": "Class",
    "test": "Test",
    "academics": "Academics"
  };

  return displayNames[section?.toLowerCase()] ||
         section?.charAt(0).toUpperCase() + section?.slice(1) ||
         "Other";
};

const AllNotification = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [tab, setTab] = useState("all");
  const [allNotification, setAllNotification] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [isNotificationFetching, setIsNotificationFetching] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [view, setView] = useState("list");
  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedNotificationId = searchParams.get("selected");
  const uniqueSections = getUniqueSections(allNotification);
  const [unReadNotifications, setUnReadNotifications] = useState([]);

  const getUnreadSectionCount = (section) => {
    return allNotification?.filter(n => n.section === section && !n.read).length;
  };

  const mainTabs = uniqueSections.slice(0, 3);
  const moreMenuItems = uniqueSections.slice(3);

  const handleMoreClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMoreClose = () => {
    setAnchorEl(null);
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    setSelectedNotification(null);

    if (newValue === "all") {
      setFilteredNotifications(allNotification);
    } else {
      const filtered = allNotification.filter(
        notification => notification.section === newValue
      );
      setFilteredNotifications(filtered);
    }
  };

  const handleNotificationSearch = (e) => {
    const { value } = e.target;
    setSelectedNotification(null);

    let notificationsToSearch = allNotification;

    if (tab !== "all" && tab !== "more") {
      notificationsToSearch = allNotification.filter(
        notification => notification.section === tab
      );
    }

    if (value === "") {
      setFilteredNotifications(notificationsToSearch);

      return;
    }

    const valArray = value.split(" ");
    const result = notificationsToSearch?.filter((data) => valArray?.every((word) => {
      const messageContent = parseMessageContent(data.message);

      return (
        data.title?.toLowerCase().includes(word.toLowerCase()) ||
          messageContent.title?.toLowerCase().includes(word.toLowerCase()) ||
          messageContent.body?.toLowerCase().includes(word.toLowerCase()) ||
          data.section?.toLowerCase().includes(word.toLowerCase())
      );
    })
    );
    setFilteredNotifications(result);
  };

  const getAllNotifications = () => {
    setIsNotificationFetching(true);
    httprequest
      .get(ALL_NOTIFICATION)
      .then((res) => {
        setIsNotificationFetching(false);
        const notifications = res?.data?.data?.items || [];
        setAllNotification(notifications);
        setFilteredNotifications(notifications);
        const unreadNotifications = notifications.filter(notif => !notif.read);
        setUnReadNotifications(unreadNotifications);
      })
      .catch((err) => {
        setIsNotificationFetching(false);
        toast.error(err?.response?.data?.message || "Failed to fetch notifications");
        setUnReadNotifications([]);
      });
  };

  const handleNotificationClick = async (notification) => {
    setSelectedNotification(notification);
    if (isMobile) {
      setView("detail");
    }

    if (notification.read) return;

    const originalNotifications = [...allNotification];

    const updatedNotifications = allNotification.map((n) => n.id === notification.id ? { ...n, read: true } : n
    );

    setAllNotification(updatedNotifications);

    const unreadNotifications = updatedNotifications.filter(notif => !notif.read);
    setUnReadNotifications(unreadNotifications);

    if (tab === "all") {
      setFilteredNotifications(updatedNotifications);
    } else {
      const filtered = updatedNotifications.filter(n => n.section === tab);
      setFilteredNotifications(filtered);
    }

    try {
      await httprequest.patch(`${ALL_NOTIFICATION}/${notification.id}`, { read: true });
    } catch (error) {
      console.error("Failed to mark notification as read:", error);

      setAllNotification(originalNotifications);
      setUnReadNotifications(originalNotifications.filter(notif => !notif.read));

      if (tab === "all") {
        setFilteredNotifications(originalNotifications);
      } else {
        const filtered = originalNotifications.filter(n => n.section === tab);
        setFilteredNotifications(filtered);
      }

      toast.error("Failed to sync read status");
    }
  };

  const handleBackClick = () => {
    setView("list");
    setSelectedNotification(null);
  };

  useEffect(() => {
    getAllNotifications();
  }, []);
  useEffect(() => {
    if (allNotification.length > 0) {
      setUnReadNotifications(allNotification.filter(notif => !notif.read));
    }
  }, [allNotification]);

  useEffect(() => {
    if (selectedNotificationId) {
      const notification = allNotification?.find(n => n.id === selectedNotificationId);
      if (notification) {
        setSelectedNotification(notification);
        if (isMobile) {
          setView("detail");
        }

        if (!notification.read) {
          const updatedNotifications = allNotification?.map((n) => n.id === notification.id ? { ...n, read: true } : n
          );
          setAllNotification(updatedNotifications);
          setFilteredNotifications(updatedNotifications);

          httprequest.patch(`${ALL_NOTIFICATION}/${notification.id}`, { read: true })
            .catch(err => {
              console.error("Failed to mark notification as read:", err);
            });
        }

        searchParams.delete("selected");
        setSearchParams(searchParams);
      }
    }
  }, [selectedNotificationId, allNotification, isMobile]);

  const getNotificationItemClasses = (notification) => {
    const isSelected = !isMobile && selectedNotification?.id === notification.id;

    return `
      ${classes.notificationItem}
      ${!notification.read ? classes.notificationItemUnread : classes.notificationItemRead}
      ${isSelected ? classes.notificationItemSelected : ""}
    `;
  };

  const renderGroupedNotifications = () => {
    if (filteredNotifications.length === 0) {
      return (
        <div style={{ textAlign: "center", color: "#8c8c8c", padding: "40px 20px" }}>
          <img src={emptynotification} alt="No messages found" />
          <div style={{ marginTop: "16px" }}>No messages found.</div>
        </div>
      );
    }

    const groupedNotifications = groupNotificationsByDate(filteredNotifications);

    return Object.entries(groupedNotifications).map(([groupKey, notifications]) => {
      if (notifications.length === 0) return null;

      return (
        <div key={groupKey}>
          <div style={{
            padding: "10px 15px",
            textAlign: "left",
            paddingBottom: "10px",
            borderBottom: "1px solid #c8c8c8ff",
            margin: "0 15px",
            fontSize: "14px",
            fontWeight: "600",
            color: "#666"
          }}
          >
            {getGroupTitle(groupKey)}
          </div>
          {notifications
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .map((notification) => {
              const messageContent = parseMessageContent(notification.message);
              const displayTitle = notification.title || messageContent.title;

              // Use the snippet for the list view
              const snippet = messageContent.snippet || "Click to view details";

              return (
                <Box
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={getNotificationItemClasses(notification)}
                >
                  {isMobile && (
                    <Box className={classes.avatar}>
                      {getInitials("Shelf21")}
                    </Box>
                  )}
                  <Box className={classes.notificationItemMain}>
                    <div className={classes.notifyTitle}>Shelf21</div>
                    <div className={classes.notifySubject}>{displayTitle}</div>
                    <div className={classes.notifyMessage}>{snippet}</div>
                  </Box>
                  <Box className={classes.notificationItemRight}>
                    <div className={classes.notifyTime}>
                      {new Date(notification.created_at).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit"
                      })}
                    </div>
                    {!notification.read && <span className={classes.unreadDot}></span>}
                  </Box>
                </Box>
              );
            })}
        </div>
      );
    });
  };
  const renderRightPanel = () => {
    if (isMobile && view !== "detail") {
      return null;
    }

    if (!isMobile && !selectedNotification) {
      return (
        <Box className={classes.rightPanelEmpty}>
          <Box>
            <img src={emptyDetails} alt="empty" />
            <h3>Select a message to view details</h3>
            <p>Nothing to display yet. Click on any message to view its content.</p>
          </Box>
        </Box>
      );
    }

    const notification = selectedNotification;
    if (!notification) return null;

    const messageContent = parseMessageContent(notification.message);
    // const displayTitle = notification.title || messageContent.title;
    // const displayBody = messageContent.body;

    return (
      <Box className={classes.rightPanel}>
        {isMobile ? (
          <Layout
            children={
              <>
                <PageTitle
                  title="All Notifications"
                  subtitle="See all your Notifications"
                  // button={
                  //   <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  //     <PopModal videoUrl="https://youtu.be/ap8lLoyZhmo" title="Push Notification" />
                  //   </div>
                  // }
                />
                <Box className={classes.detailBodyMobile}>
                  <Box className={classes.rightPanelHeaderMobile}>
                    <Box className={classes.backButton} onClick={handleBackClick}>
                      <Icon icon="mdi:arrow-left" />
                      <span>Go back</span>
                    </Box>
                    <Icon icon="mdi:dots-vertical" className={classes.detailHeaderMobileOptions} />
                  </Box>
                  <div className={classes.notifMobileHeaderContainer}>
                    <div className={classes.notifleft}>
                      <div className={classes.notifAvatar}>
                        {getInitials("Shelf21")}
                      </div>
                      <div className={classes.notifUserInfo}>
                        <div className={classes.notifFrom}>Shelf21</div>
                        <div className={classes.notifTo}>To: You</div>
                      </div>
                    </div>
                    <div className={classes.notifDate}>
                      {formatDateTime(notification.created_at)}
                      <Icon icon="mdi:dots-vertical" className={classes.detailHeaderMobileOptions} />
                    </div>
                  </div>
                  {/* <div className={classes.detailBodyMobileSubject}>
                    <h2>{displayTitle}</h2>
                  </div> */}
                  <Box
                    className={classes.htmlMessageContainer}
                    dangerouslySetInnerHTML={{ __html: messageContent.body }}
                  />
                  <div className={classes.securityMobileSection}>
                    <div>
                      If you see any suspicious activity on your account, please{" "}
                      <span className={classes.securityLink}>
                        contact us via our support site
                      </span>.
                    </div>
                    <div className={classes.teamSignature}>
                      - The shelf21 team
                    </div>
                  </div>
                </Box>
                <Box className={classes.detailFooter}>
                  {/* <Box className={classes.socialIconsWrapper}>
                    <div className={classes.socialWrapper}>
                      <a
                        href="https://www.facebook.com/profile.php?id=61567083193107&mibextid=LQQJ4d"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.socialLink}
                      >
                        <Icon icon="uil:facebook-f" className={classes.socialIcon} />
                      </a>
                    </div>
                    <div className={classes.socialWrapper}>
                      <a
                        href="https://x.com/shelf21_/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.socialLink}
                      >
                        <Icon icon="mdi:twitter" className={classes.socialIcon} />
                      </a>
                    </div>
                    <div className={classes.socialWrapper}>
                      <a
                        href="https://www.instagram.com/shelf.21/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.socialLink}
                      >
                        <Icon icon="mdi:instagram" className={classes.socialIcon} />
                      </a>
                    </div>
                  </Box> */}
                  <Box className={classes.detailFooterText}>
                    Powered By                 <img src={logo} className={classes.subjectimage} alt="Shelf21 Logo" />
                  </Box>
                </Box>
              </>
            }
          />
        ) : (
          <>
            <Box className={classes.detailBody}>
              <Box className={classes.rightPanelHeaderMobile}>
                <Box className={classes.backButton} onClick={handleBackClick}>
                  <Icon icon="mdi:arrow-left" />
                  <span>Go back</span>
                </Box>
              </Box>
              <div className={classes.notifHeaderContainer}>
                <div className={classes.notifleft}>
                  <div className={classes.notifAvatar}>
                    {getInitials("Shelf21")}
                  </div>
                  <div className={classes.notifUserInfo}>
                    <div className={classes.notifFrom}>Shelf21</div>
                    <div className={classes.notifTo}>To: You</div>
                  </div>
                </div>
                <div className={classes.notifDate}>
                  {formatDateTime(notification.created_at)}
                  <Icon icon="mdi:dots-vertical" className={classes.detailHeaderMobileOptions} />
                </div>
              </div>
              {/* <div className={classes.detailBodySubject}>
                <h2>{displayTitle}</h2>
                <img src={logo} className={classes.subjectimage} alt="Shelf21 Logo" />
              </div> */}
              <Box
                className={classes.htmlMessageContainer}
                dangerouslySetInnerHTML={{ __html: messageContent.body }}
              />
              <div className={classes.securitySection}>
                <div>
                  If you see any suspicious activity on your account, please{" "}
                  <span className={classes.securityLink}>
                    contact us via our support site
                  </span>.
                </div>
                <div className={classes.teamSignature}>
                  - The shelf21 team
                </div>
              </div>
            </Box>
            <Box className={classes.detailFooter}>
              {/* <Box className={classes.socialIconsWrapper}>
                <div className={classes.socialWrapper}>
                  <a
                    href="https://www.facebook.com/profile.php?id=61567083193107&mibextid=LQQJ4d"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.socialLink}
                  >
                    <Icon icon="uil:facebook-f" className={classes.socialIcon} />
                  </a>
                </div>
                <div className={classes.socialWrapper}>
                  <a
                    href="https://x.com/shelf21_/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.socialLink}
                  >
                    <Icon icon="mdi:twitter" className={classes.socialIcon} />
                  </a>
                </div>
                <div className={classes.socialWrapper}>
                  <a
                    href="https://www.instagram.com/shelf.21/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.socialLink}
                  >
                    <Icon icon="mdi:instagram" className={classes.socialIcon} />
                  </a>
                </div>
              </Box> */}
              <Box className={classes.detailFooterText}>
                    Powered By                 <img src={logo} className={classes.subjectimage} alt="Shelf21 Logo" />
              </Box>
            </Box>
          </>
        )}
      </Box>
    );
  };

  return (
    <Layout
      children={
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <PageTitle
            title="All Notifications"
            subtitle="See all your Notifications"
            button={
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                <PopModal videoUrl="https://youtu.be/ap8lLoyZhmo" title="Push Notification" />
              </div>
            }
          />

          {!isNotificationFetching && allNotification?.length === 0 ? (
            <div className="page-content" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div className={classes.notfound}>
                <NoData title="Empty Notification"
                  subtitle="No messages found."
                  img={emptynotification}
                />
              </div>
            </div>
          ) : (
            <>
              {(!isMobile || (isMobile && view === "list")) && (
                <>
                  <Box className={classes.tabContainer}>
                    <MuiButton
                      className={`${classes.tabButton} ${tab === "all" ? classes.tabButtonSelected : ""}`}
                      onClick={(e) => handleTabChange(e, "all")}
                    >
                      All
                      <span className={`${classes.tabCount} ${
                        tab === "all" ? classes.tabCountSelected : ""
                      }`}
                      >{unReadNotifications?.length}
                      </span>
                    </MuiButton>

                    {mainTabs.map((section) => {
                      const displayName = getSectionDisplayName(section);
                      const unreadSectionCount = getUnreadSectionCount(section);

                      return (
                        <MuiButton
                          key={section}
                          className={`${classes.tabButton} ${tab === section ? classes.tabButtonSelected : ""}`}
                          onClick={(e) => handleTabChange(e, section)}
                        >
                          {displayName}
                          {unreadSectionCount > 0 && (
                            <span className={`${classes.tabCount} ${
                              tab === section ? classes.tabCountSelected : ""
                            }`}
                            >{unreadSectionCount}
                            </span>
                          )}
                        </MuiButton>
                      );
                    })}

                    {moreMenuItems.length > 0 && (
                      <MuiButton
                        className={`${classes.tabButton} ${tab === "more" ? classes.tabButtonSelected : ""}`}
                        onClick={handleMoreClick}
                        endIcon={<KeyboardArrowDown sx={{ color: "#8A93A3", fontSize: "28px" }} />}
                      >
                        More
                      </MuiButton>
                    )}

                    <Menu
                      anchorEl={anchorEl}
                      open={isMenuOpen}
                      onClose={handleMoreClose}
                      getContentAnchorEl={null}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                      className={classes.customMenu}
                    >
                      {moreMenuItems.map((section) => {
                        const displayName = getSectionDisplayName(section);
                        const unreadSectionCount = getUnreadSectionCount(section);

                        return (
                          <MenuItem
                            key={section}
                            className={classes.customMenuItem}
                            onClick={() => {
                              handleTabChange(null, section);
                              handleMoreClose();
                            }}
                          >
                            {displayName}
                            {unreadSectionCount > 0 && (
                              <span className={classes.menuBadge}>{unreadSectionCount}</span>
                            )}
                          </MenuItem>
                        );
                      })}
                    </Menu>
                  </Box>
                </>
              )}

              <Box className={classes.mainWrapper}>
                {(!isMobile || (isMobile && view === "list")) && (
                  <Box className={classes.leftPanel}>
                    <Box className={classes.searchWrapper}>
                      <Paper
                        sx={{
                          marginTop: "15px",
                          marginBottom: "10px",
                          p: "4px",
                          display: "flex",
                          alignItems: "center",
                          backgroundColor: "#F5F6F7",
                          boxShadow: "none",
                          border: "none"
                        }}
                      >
                        <Search sx={{ color: "#8A93A3", margin: "0 8px" }} />
                        <InputBase
                          placeholder="Search messages..."
                          onChange={handleNotificationSearch}
                          fullWidth
                          sx={{ flex: 1 }}
                        />
                      </Paper>
                    </Box>

                    <Box className={classes.notificationListWrapper}>
                      {isNotificationFetching ? (
                        <div style={{ padding: "20px", textAlign: "center" }}>Loading...</div>
                      ) : (
                        renderGroupedNotifications()
                      )}
                    </Box>
                  </Box>
                )}

                {renderRightPanel()}
              </Box>
            </>
          )}
          {/* <ToastContainer /> */}
        </div>
      }
    />
  );
};

export default AllNotification;
